import React,{useState,useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {motion} from 'framer-motion'
import {useAuth} from '../contexts/AuthContext'
import {useUserData} from '../hooks/useUserData'
import {getCurrentPhase,getMoonPhaseEmoji,getTrackingMode} from '../utils/cycleCalculator'
import phasesData from '../data/phases.json'
import allPhasesData from '../data/allPhases.json'
import moonPhasesData from '../data/moonPhases.json'
import StripePaymentButton from './StripePaymentButton'

const HomeScreen=()=> {
  const navigate=useNavigate()
  const {user}=useAuth()
  const {userProfile,currentPhase: userCurrentPhase,loading}=useUserData()
  const [currentPhase,setCurrentPhase]=useState(null)
  const [isPersonalized,setIsPersonalized]=useState(false)
  const [trackingMode,setTrackingMode]=useState(null)
  const [isPremium,setIsPremium]=useState(false)

  useEffect(()=> {
    // Check premium status
    const premiumStatus=localStorage.getItem('isPremium')==='true'
    setIsPremium(premiumStatus)

    if (!loading) {
      if (user && userProfile && userCurrentPhase) {
        // Use authenticated user's data
        let phaseData=null
        if (userProfile.tracking_mode==='moon') {
          phaseData=moonPhasesData.phases.find(p=> 
            p.moonPhase===userCurrentPhase.moonPhase
          )
        } else if (userProfile.tracking_mode==='cycle') {
          phaseData=allPhasesData.phases.find(p=> 
            p.menstrualPhase===userCurrentPhase.menstrualPhase
          )
        }

        if (phaseData) {
          setCurrentPhase({...phaseData,...userCurrentPhase})
          setIsPersonalized(true)
          setTrackingMode(userProfile.tracking_mode)
        }
      } else {
        // Fallback to local storage for non-authenticated users
        const mode=getTrackingMode()
        setTrackingMode(mode)
        const personalizedPhase=getCurrentPhase()

        if (personalizedPhase) {
          let phaseData=null
          if (mode==='moon') {
            phaseData=moonPhasesData.phases.find(p=> 
              p.moonPhase===personalizedPhase.moonPhase
            )
          } else if (mode==='cycle') {
            phaseData=allPhasesData.phases.find(p=> 
              p.menstrualPhase===personalizedPhase.menstrualPhase
            )
          }

          if (phaseData) {
            setCurrentPhase({...phaseData,...personalizedPhase})
            setIsPersonalized(true)
          } else {
            setCurrentPhase(phasesData.phases[0])
            setIsPersonalized(false)
          }
        } else {
          setCurrentPhase(phasesData.phases[0])
          setIsPersonalized(false)
        }
      }
    }
  },[user,userProfile,userCurrentPhase,loading])

  const scenarios=[
    {id: 'business',label: 'Running a business',icon: '💼'},
    {id: 'creation',label: 'In creation mode',icon: '✨'},
    {id: 'kids',label: 'Kids with you today',icon: '👶'},
    {id: 'diary',label: 'Full diary',icon: '📅'},
    {id: 'spiritual',label: 'Spiritual practice',icon: '🕯️'}
  ]

  const handleScenarioSelect=(scenarioId)=> {
    navigate(`/scenario/${scenarioId}`)
  }

  const handlePersonalize=()=> {
    if (user) {
      navigate('/profile')
    } else if (isPremium) {
      navigate('/setup')
    } else {
      navigate('/unlock')
    }
  }

  const handleAuthAction=()=> {
    if (user) {
      navigate('/profile')
    } else if (isPremium) {
      navigate('/auth')
    } else {
      navigate('/unlock')
    }
  }

  if (loading || !currentPhase) {
    return (
      <div className="min-h-screen bg-white px-6 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your guidance...</p>
        </div>
      </div>
    )
  }

  const moonEmoji=getMoonPhaseEmoji(currentPhase.moonPhase)

  const getPhaseSubtitle=()=> {
    if (!isPersonalized) return '(Demo phase)'
    if (trackingMode==='moon') {
      return `(${currentPhase.moonPhase} - Lunar Day ${currentPhase.lunarDay})`
    } else if (trackingMode==='cycle') {
      return `(${currentPhase.moonPhase} + ${currentPhase.menstrualPhase})`
    }
    return ''
  }

  const getCycleDayText=()=> {
    if (!isPersonalized) return null
    if (trackingMode==='cycle' && currentPhase.cycleDay) {
      return `Day ${currentPhase.cycleDay} of your cycle`
    } else if (trackingMode==='moon' && currentPhase.lunarDay) {
      return `Lunar day ${currentPhase.lunarDay}`
    }
    return null
  }

  return (
    <div className="min-h-screen bg-white px-6 py-8">
      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        transition={{duration: 0.8}}
        className="max-w-md mx-auto"
      >
        {/* Top Banner */}
        <div className="text-center mb-12">
          {/* Account Status */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-2">
              {user ? (
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {user.user_metadata?.full_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm text-gray-600">
                    Welcome back,{user.user_metadata?.full_name?.split(' ')[0] || 'there'}!
                  </span>
                </div>
              ) : isPremium ? (
                <span className="text-xs bg-gold text-white px-3 py-1 rounded-full">
                  Premium Guest
                </span>
              ) : (
                <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                  Guest Mode
                </span>
              )}
            </div>
            <button
              onClick={handleAuthAction}
              className="text-sm text-gold hover:text-black transition-colors"
            >
              {user ? 'Profile' : isPremium ? 'Create Account' : 'Get Premium'}
            </button>
          </div>

          <h1 className="italiana-font text-3xl text-black mb-4 leading-tight">
            Use Your {trackingMode==='moon' ? 'Natural Rhythm' : 'Cycle'} as a Superpower — Not a Setback {moonEmoji}
          </h1>

          <div className="mb-6">
            <h2 className="text-lg font-medium text-black mb-2">
              You're in <span className="gold-accent font-semibold">{currentPhase.name}</span>
            </h2>
            <p className="text-sm text-gray-600">
              {getPhaseSubtitle()}
            </p>
            {getCycleDayText() && (
              <p className="text-xs text-gold font-medium mt-1">
                {getCycleDayText()}
              </p>
            )}
          </div>

          <div className="bg-gray-50 p-6 rounded-lg mb-8">
            <p className="text-sm leading-relaxed text-gray-700">
              {currentPhase.description}
            </p>
          </div>

          {!isPremium && (
            <div className="mb-6">
              <StripePaymentButton 
                className="mb-4 w-full"
                paymentLink="https://buy.stripe.com/00wbJ18S06EwbMR3Fwgw005"
              >
                🔐 Get Complete System + Account Access
              </StripePaymentButton>
            </div>
          )}

          {isPremium && !user && (
            <motion.button
              onClick={()=> navigate('/auth')}
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              className="mb-6 px-6 py-3 bg-gold text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
            >
              🔐 Create Your Secure Account
            </motion.button>
          )}

          {!isPersonalized && (
            <motion.button
              onClick={handlePersonalize}
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              className="mb-8 px-6 py-3 bg-gold text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
            >
              📅 Get Your Personal Phase (30 seconds)
            </motion.button>
          )}
        </div>

        {/* Scenario Selection */}
        <div className="mb-8">
          <h3 className="italiana-font text-xl text-black mb-6 text-center">
            What kind of day are you having?
          </h3>
          <div className="space-y-4">
            {scenarios.map((scenario,index)=> (
              <motion.button
                key={scenario.id}
                initial={{opacity: 0,x: -20}}
                animate={{opacity: 1,x: 0}}
                transition={{duration: 0.5,delay: index * 0.1}}
                onClick={()=> handleScenarioSelect(scenario.id)}
                className="w-full p-4 text-left border border-gray-200 rounded-lg hover-gold transition-all hover:shadow-lg"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-xl">{scenario.icon}</span>
                  <span className="font-medium text-black">{scenario.label}</span>
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {isPersonalized && (
          <div className="text-center">
            <button
              onClick={handlePersonalize}
              className="text-sm text-gray-500 hover:text-black transition-colors"
            >
              Update {trackingMode==='moon' ? 'tracking preferences' : 'cycle information'}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default HomeScreen